import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { PrayerDayDetail, PrayerOverview, PrayerRequest } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { toLocalInput } from '../calendar/dates';
import { formatWhen } from '../events/shared';
import { useMe } from '../session/SessionProvider';
import { dayTitle, printUrl, whatsappText, whatsappUrl } from './share';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';
import { audienceLabel, ConvocationFields, type Audience } from '../../shared/ui/ConvocationFields';

const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** Crear (una o cada semana) o editar una jornada de oración. */
export function PrayerDayFormPage() {
  const { sessionId } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const navigate = useNavigate();
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      const overview = await api.get<PrayerOverview>(`/prayer/churches/${churchId}/sessions`, signal);
      const day = sessionId ? await api.get<PrayerDayDetail>(`/prayer/churches/${churchId}/sessions/${sessionId}`, signal) : null;
      const requests = overview.team.member ? (await api.get<{ items: PrayerRequest[] }>(`/prayer/churches/${churchId}/requests?list=team`, signal)).items : [];
      return { overview, day, requests };
    },
    [churchId, sessionId],
  );
  const [mode, setMode] = useState<'presencial' | 'virtual'>('presencial');
  const [repeat, setRepeat] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [selected, setSelected] = useState<string[]>([]);
  const [convocation, setConvocation] = useState<{ audience: Audience; chosen: string[] }>({ audience: 'iglesia', chosen: [] });
  const [first, setFirst] = useState(() => {
    const d = new Date(Date.now() + 2 * 86400_000);
    d.setHours(19, 0, 0, 0);
    return toLocalInput(d);
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const loadedDay = data.status === 'ready' ? data.data?.day : null;
  useEffect(() => {
    if (loadedDay) {
      setMode(loadedDay.mode);
      setFirst(toLocalInput(new Date(loadedDay.startsAt)));
      setConvocation({ audience: loadedDay.audience, chosen: loadedDay.chosen });
    }
  }, [loadedDay]);

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  if (!data.data) return null;
  const { overview, day, requests } = data.data;
  if (!overview.team.leader) return <div className="page"><EmptyState title="Solo el líder o co-líder del ministerio de Oración organiza las jornadas." /></div>;
  const start = new Date(first);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const minutes = Number(f.get('minutes') || 60);
    if (Number.isNaN(start.getTime())) return setError('Indica la fecha y hora.');
    const fields = { title: t('title'), mode, placeName: mode === 'presencial' ? t('placeName') : null, address: mode === 'presencial' ? t('address') : null, mapsUrl: mode === 'presencial' ? t('mapsUrl') : null, meetingUrl: mode === 'virtual' ? t('meetingUrl') : null, leaderId: t('leaderId'), notes: t('notes'), audience: convocation.audience, people: convocation.chosen };
    const dateAt = (i: number) => {
      const s = new Date(start);
      s.setDate(s.getDate() + i * 7);
      return { startsAt: s.toISOString(), endsAt: new Date(s.getTime() + minutes * 60_000).toISOString() };
    };
    setBusy(true);
    setError(null);
    try {
      if (day) {
        await api.put(`/prayer/churches/${churchId}/sessions/${day.id}`, { ...fields, ...dateAt(0) });
        navigate(`/app/oracion/jornadas/${day.id}`);
      } else {
        const dates = Array.from({ length: repeat ? Math.min(Math.max(weeks, 1), 60) : 1 }, (_, i) => dateAt(i));
        const { ids } = await api.post<{ ids: string[] }>(`/prayer/churches/${churchId}/sessions`, { ...fields, dates, requestIds: selected });
        navigate(`/app/oracion/jornadas/${ids[0]}`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const minutesDefault = day ? Math.round((new Date(day.endsAt).getTime() - new Date(day.startsAt).getTime()) / 60_000) : 60;
  return (
    <div className="page">
      <p className="small">
        <Link to={day ? `/app/oracion/jornadas/${day.id}` : '/app/oracion'}>← Volver</Link>
      </p>
      <h1>{day ? 'Editar jornada' : 'Nueva jornada de oración'}</h1>
      <form className="form event-form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Card title="Cuándo y dónde">
          <Field label="Título (opcional)" name="title" maxLength={150} defaultValue={day?.title ?? ''} placeholder="Martes de oración" />
          <div className="form__row">
            <Field label={repeat ? 'Primera jornada' : 'Fecha y hora'} type="datetime-local" value={first} onChange={(e) => setFirst(e.target.value)} required />
            <Field label="Duración (minutos)" name="minutes" type="number" min={15} max={720} defaultValue={minutesDefault} />
          </div>
          {!day ? (
            <>
              <label className="check">
                <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} /> Repetir cada semana
              </label>
              {repeat ? (
                <>
                  <Field label="¿Cuántas semanas?" type="number" min={1} max={60} value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 1)} />
                  {!Number.isNaN(start.getTime()) ? (
                    <p className="small">
                      Todos los {WEEKDAYS[start.getDay()]} a las {start.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}, {Math.min(Math.max(weeks, 1), 60)} semanas.
                    </p>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          <div className="segmented" role="radiogroup" aria-label="Modalidad">
            <label className="check">
              <input type="radio" checked={mode === 'presencial'} onChange={() => setMode('presencial')} /> Presencial
            </label>
            <label className="check">
              <input type="radio" checked={mode === 'virtual'} onChange={() => setMode('virtual')} /> Virtual
            </label>
          </div>
          {mode === 'presencial' ? (
            <>
              <Field label="Lugar" name="placeName" maxLength={200} defaultValue={day?.placeName ?? ''} placeholder="Templo, salón de oración…" />
              <Field label="Dirección" name="address" maxLength={300} defaultValue={day?.address ?? ''} />
              <Field label="Ubicación en el mapa" name="mapsUrl" type="url" maxLength={500} defaultValue={day?.mapsUrl ?? ''} placeholder="https://maps.app.goo.gl/…" />
            </>
          ) : (
            <Field label="Enlace de la reunión" name="meetingUrl" type="url" maxLength={500} defaultValue={day?.meetingUrl ?? ''} placeholder="https://meet.google.com/… o https://zoom.us/…" />
          )}
          <div className="field">
            <label htmlFor="pd-leader">¿Quién dirige?</label>
            <SearchSelect id="pd-leader" name="leaderId" defaultValue={day?.leader?.personId ?? ''} emptyLabel="Sin definir" options={personOptions(overview.people)} />
          </div>
          <div className="field">
            <label htmlFor="pd-notes">Notas (opcional)</label>
            <textarea id="pd-notes" name="notes" rows={2} maxLength={2000} defaultValue={day?.notes ?? ''} />
          </div>
        </Card>
        <Card title="Convocados">
          <ConvocationFields idPrefix="pd" ministry="Oración" people={overview.people} audience={convocation.audience} chosen={convocation.chosen} onChange={setConvocation} />
        </Card>
        {!day && requests.length > 0 ? (
          <Card title="Peticiones para esta jornada">
            <RequestPicker requests={requests} value={selected} onChange={setSelected} />
          </Card>
        ) : null}
        <div className="actions">
          <Button type="submit" busy={busy}>
            {day ? 'Guardar cambios' : 'Crear jornada'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function RequestPicker({ requests, value, onChange }: { requests: PrayerRequest[]; value: string[]; onChange: (ids: string[]) => void }) {
  return (
    <>
      <div className="actions">
        <Button type="button" variant="ghost" onClick={() => onChange(value.length === requests.length ? [] : requests.map((r) => r.id))}>
          {value.length === requests.length ? 'Quitar selección' : 'Seleccionar todas'}
        </Button>
        <span className="small muted">{value.length} elegidas</span>
      </div>
      <ul className="picker__list">
        {requests.map((r) => (
          <li key={r.id}>
            <label className="check" htmlFor={`pick-${r.id}`}>
              <input id={`pick-${r.id}`} type="checkbox" checked={value.includes(r.id)} onChange={() => onChange(value.includes(r.id) ? value.filter((x) => x !== r.id) : [...value, r.id])} />
              <span>
                {r.body.length > 90 ? `${r.body.slice(0, 89)}…` : r.body}
                <span className="muted small"> · {r.author ? r.author.name : 'Anónima'}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Una jornada: datos, peticiones elegidas (solo el equipo), PDF y WhatsApp. */
export function PrayerDayPage() {
  const { sessionId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/prayer/churches/${churchId}/sessions/${sessionId}`;
  const data = useAsync((signal) => (churchId ? api.get<PrayerDayDetail>(base, signal) : Promise.resolve(null)), [base]);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pool = useAsync(
    (signal) => (choosing && churchId ? api.get<{ items: PrayerRequest[] }>(`/prayer/churches/${churchId}/requests?list=team`, signal).then((r) => r.items) : Promise.resolve([])),
    [choosing, churchId],
  );
  const [selected, setSelected] = useState<string[]>([]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setChoosing(false);
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/oracion">← Oración</Link>
      </p>
      <header className="page__header page__header--actions">
        <div>
          <h1>{dayTitle(d)}</h1>
          <p className="muted">
            {formatWhen(d.startsAt, d.endsAt)} {d.canceled ? <span className="badge">Cancelada</span> : null}
          </p>
        </div>
        {d.canManage ? (
          <Link className="btn btn--secondary" to={`/app/oracion/jornadas/${d.id}/editar`}>
            Editar
          </Link>
        ) : null}
      </header>
      {error ? <Alert>{error}</Alert> : null}
      <Card title="Detalles">
        <dl className="details">
          <dt>Modalidad</dt>
          <dd>{d.mode === 'virtual' ? 'Virtual' : 'Presencial'}</dd>
          {d.mode === 'virtual' && d.meetingUrl ? (
            <>
              <dt>Enlace</dt>
              <dd>
                <a href={d.meetingUrl} target="_blank" rel="noopener noreferrer">
                  Entrar a la reunión
                </a>
              </dd>
            </>
          ) : null}
          {d.mode === 'presencial' && (d.placeName || d.address) ? (
            <>
              <dt>Dónde</dt>
              <dd>
                {d.placeName ? <strong>{d.placeName}</strong> : null}
                {d.address ? <span className="block">{d.address}</span> : null}
                {d.mapsUrl ? (
                  <a href={d.mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                    Cómo llegar
                  </a>
                ) : null}
              </dd>
            </>
          ) : null}
          <dt>Dirige</dt>
          <dd>{d.leader ? d.leader.name : <span className="muted">Sin definir</span>}</dd>
          <dt>Convocados</dt>
          <dd>
            {audienceLabel(d.audience, 'Oración', d.convoked.length)}
            {d.audience !== 'iglesia' && d.convoked.length ? <span className="small muted"> · {d.convoked.map((c) => c.name).join(', ')}</span> : null}
          </dd>
          {d.notes ? (
            <>
              <dt>Notas</dt>
              <dd className="pre-line">{d.notes}</dd>
            </>
          ) : null}
        </dl>
      </Card>

      {d.requests ? (
        <Card title={`Peticiones de esta jornada (${d.requests.length})`}>
          {choosing ? (
            pool.status === 'ready' ? (
              <>
                <RequestPicker requests={pool.data} value={selected} onChange={setSelected} />
                <div className="actions">
                  <Button busy={busy} onClick={() => void run(() => api.put(`${base}/requests`, { requestIds: selected }))}>
                    Guardar selección
                  </Button>
                  <Button variant="ghost" onClick={() => setChoosing(false)}>
                    Cancelar
                  </Button>
                </div>
              </>
            ) : (
              <Loading />
            )
          ) : (
            <>
              {d.requests.length === 0 ? <p className="muted">Aún no se han elegido peticiones.</p> : null}
              <ol className="prayer-list">
                {d.requests.map((r) => (
                  <li key={r.id}>
                    <span className="pre-line">{r.body}</span>
                    <span className="muted small"> — {r.author ? r.author.name : 'Anónima'}</span>
                  </li>
                ))}
              </ol>
              <div className="actions">
                {d.canManage ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelected(d.requests!.map((r) => r.id));
                      setChoosing(true);
                    }}
                  >
                    Elegir peticiones
                  </Button>
                ) : null}
                {d.requests.length > 0 ? (
                  <>
                    <Link className="btn btn--secondary" to={printUrl({ jornada: d.id })} target="_blank">
                      Exportar PDF
                    </Link>
                    <a className="btn btn--primary" href={whatsappUrl(whatsappText(d.requests, dayTitle(d), d))} target="_blank" rel="noopener noreferrer">
                      Enviar por WhatsApp
                    </a>
                  </>
                ) : null}
              </div>
            </>
          )}
        </Card>
      ) : null}

      {d.canManage ? (
        <p>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              if (window.confirm('¿Cancelar esta jornada?')) void run(() => api.post(`${base}/cancel`, {}));
            }}
          >
            Cancelar jornada
          </Button>
        </p>
      ) : null}
    </div>
  );
}

/** Hoja imprimible (Guardar como PDF) con las peticiones de una jornada o las elegidas. */
export function PrayerPrintPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [params] = useSearchParams();
  const jornada = params.get('jornada');
  const ids = (params.get('ids') ?? '').split(',').filter(Boolean);
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      if (jornada) {
        const d = await api.get<PrayerDayDetail>(`/prayer/churches/${churchId}/sessions/${jornada}`, signal);
        return { title: dayTitle(d), subtitle: formatWhen(d.startsAt, d.endsAt), leader: d.leader?.name ?? null, requests: d.requests ?? [] };
      }
      const all = (await api.get<{ items: PrayerRequest[] }>(`/prayer/churches/${churchId}/requests?list=team`, signal)).items;
      return { title: 'Peticiones de oración', subtitle: new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' }), leader: null, requests: all.filter((r) => ids.includes(r.id)) };
    },
    [churchId, jornada, ids.join(',')],
  );
  const ready = data.status === 'ready' && data.data !== null;
  useEffect(() => {
    if (ready && typeof window.print === 'function' && !navigator.webdriver) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [ready]);

  return (
    <div className="print-sheet">
      <div className="print-sheet__bar no-print">
        <Button onClick={() => window.print()}>Imprimir o guardar PDF</Button>
        <span className="small muted">En el cuadro de impresión elige «Guardar como PDF».</span>
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} /> : null}
      {ready ? (
        <>
          <header className="print-sheet__head">
            <Logo />
            <div>
              <h1>{data.data!.title}</h1>
              <p>
                {me.activeContext?.churchName} · {data.data!.subtitle}
                {data.data!.leader ? ` · Dirige ${data.data!.leader}` : ''}
              </p>
            </div>
          </header>
          {data.data!.requests.length === 0 ? <p>No hay peticiones.</p> : null}
          <ol className="prayer-list prayer-list--print">
            {data.data!.requests.map((r) => (
              <li key={r.id}>
                <span className="pre-line">{r.body}</span>
                <span className="muted"> — {r.author ? r.author.name : 'Anónima'}</span>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </div>
  );
}
